import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, Checkbox, CircularProgress, Input, Grid, Card, CardMedia, CardContent, CardActions, IconButton, MenuItem,
    Snackbar, Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
// Replaced react-icons/fi with Material UI icons
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion'; // Import motion and AnimatePresence
import { BASE_URL } from '../../utils/endpoints';

// Helper function to truncate text for display
const truncateText = (text, maxLength = 70) => {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '…' : text;
};

// Framer Motion variants for the main container's entrance animation
const containerVariants = {
    hidden: { opacity: 0, y: 20 }, // Start slightly below and invisible
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.5, // Animation duration
            ease: "easeOut", // Easing function
            when: "beforeChildren", // Animate container before its children
            staggerChildren: 0.1 // Stagger the appearance of children by 0.1 seconds
        }
    }
};

// Framer Motion variants for individual item (Card) animation
const itemVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.8 }, // Start hidden, further down, and slightly smaller
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
            duration: 0.4, // Duration for each item
            ease: "easeOut" // Easing function
        }
    },
    exit: { // Animation for when an item is removed (e.g., deleted)
        opacity: 0,
        scale: 0.5,
        transition: {
            duration: 0.3, // Quick exit animation
            ease: "easeIn"
        }
    }
};

const GalleryList = () => {
    // Pagination states
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(8);
    // Data states
    const [allImages, setAllImages] = useState([]); // Stores all fetched images
    const [displayImages, setDisplayImages] = useState([]); // Images currently displayed on the page
    // UI states
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedImages, setSelectedImages] = useState([]); // IDs of selected images for bulk actions
    const [searchTerm, setSearchTerm] = useState(''); // Search input value

    // Snackbar state for showing success/error messages
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    // Dialog state for delete confirmation
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    // State to hold the ID(s) for deletion (single or bulk)
    const [itemsToDelete, setItemsToDelete] = useState([]);
    // State to differentiate between single and bulk delete
    const [isBulkDelete, setIsBulkDelete] = useState(false);

    // --- API Call: Fetch Images ---
    const fetchImages = async () => {
        try {
            setLoading(true); // Indicate loading start
            setError(null); // Clear previous errors
            // Construct URL with search term and a large limit to fetch all relevant images
            const url = `${BASE_URL}/images?page=1&limit=500${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ''}`;
            const response = await axios.get(url);

            // Normalize response data: sometimes it's direct array, sometimes nested in 'data'
            const imageData = Array.isArray(response.data) ? response.data : Array.isArray(response.data.data) ? response.data.data : [];
            // Format images to a consistent structure
            const formattedImages = imageData.map(image => ({
                id: image._id || `fallback-${Math.random()}`, // Use _id or a fallback
                fileUrl: image.file_url || '', // Image URL
                fileName: image.file_name || 'Unnamed File', // Image name
            })).reverse();
            setAllImages(formattedImages); // Update the full list of images
        } catch (error) {
            console.error('Error fetching images:', error);
            setError('Failed to fetch images.'); // Set error message
            setSnackbar({ open: true, message: 'Failed to fetch images.', severity: 'error' }); // Show snackbar error
        } finally {
            setLoading(false); // End loading
        }
    };

    // --- useEffect: Update displayed images based on filters/pagination ---
    useEffect(() => {
        // Filter `allImages` based on the current `searchTerm`
        const filtered = allImages.filter(image =>
            image.fileName.toLowerCase().includes(searchTerm.toLowerCase())
        );
        // Calculate pagination slice
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        // Update `displayImages` for the current page
        setDisplayImages(filtered.slice(startIndex, endIndex));
    }, [allImages, page, rowsPerPage, searchTerm]); // Dependencies for re-calculation

    // --- useEffect: Initial data fetch and refetch on search term change ---
    useEffect(() => {
        fetchImages();
    }, [searchTerm]); // Re-fetch all images from API when searchTerm changes

    // --- Event Handlers ---

    // Handle file upload
    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return; // If no file selected, exit

        const formData = new FormData();
        formData.append('file', file); // Append the file to form data

        try {
            setLoading(true); // Start loading indicator
            const response = await axios.post(`${BASE_URL}/image`, formData);
            // Show success snackbar
            setSnackbar({ open: true, message: response.data.message || 'File uploaded successfully.', severity: 'success' });
            fetchImages(); // Re-fetch images to update the gallery
        } catch (error) {
            console.error('Error uploading file:', error);
            // Show error snackbar
            setSnackbar({ open: true, message: 'Error uploading file.', severity: 'error' });
        } finally {
            setLoading(false); // End loading indicator
        }
    };

    // Prepare for single image deletion by opening dialog
    const handleSingleDelete = (imageId) => {
        setIsBulkDelete(false); // Not a bulk delete
        setItemsToDelete([imageId]); // Set the single ID to delete
        setOpenDeleteDialog(true); // Open the confirmation dialog
    };

    // Prepare for bulk deletion by opening dialog
    const handleBulkDelete = () => {
        if (selectedImages.length === 0) return; // If nothing selected, do nothing
        setIsBulkDelete(true); // It's a bulk delete
        setItemsToDelete(selectedImages); // Set all selected IDs to delete
        setOpenDeleteDialog(true); // Open the confirmation dialog
    };

    // Execute deletion after confirmation
    const handleDeleteConfirmed = async () => {
        setOpenDeleteDialog(false); // Close the dialog immediately

        try {
            setLoading(true); // Start loading
            // Create an array of delete promises for all items
            const deletePromises = itemsToDelete.map(id =>
                axios.delete(`${BASE_URL}/image/${id}`)
            );
            await Promise.all(deletePromises); // Wait for all delete operations to complete
            // Show success snackbar
            setSnackbar({ open: true, message: 'File(s) deleted successfully.', severity: 'success' });
            fetchImages(); // Re-fetch images to reflect changes
            setSelectedImages([]); // Clear selections after deletion
            setItemsToDelete([]); // Clear items to delete
        } catch (error) {
            console.error('Error deleting file(s):', error);
            // Show error snackbar
            setSnackbar({ open: true, message: 'Error deleting file(s).', severity: 'error' });
        } finally {
            setLoading(false); // End loading
        }
    };

    // Close the delete confirmation dialog
    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false); // Close dialog
        setItemsToDelete([]); // Clear items
        setIsBulkDelete(false); // Reset bulk flag
    };

    // Copy file URL to clipboard
    const copyToClipboard = (url) => {
        navigator.clipboard.writeText(url)
            .then(() => {
                setSnackbar({ open: true, message: 'File link copied to clipboard!', severity: 'success' });
            })
            .catch(err => {
                console.error('Failed to copy text: ', err);
                setSnackbar({ open: true, message: 'Failed to copy link.', severity: 'error' });
            });
    };

    // Handle page change for pagination
    const handlePageChange = (newPage) => {
        setPage(newPage);
        setSelectedImages([]); // Clear selections on page change
    };

    // Handle rows per page change for pagination
    const handleRowsPerPageChange = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0); // Reset to first page
        setSelectedImages([]); // Clear selections on page change
    };

    // Handle search input change
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(0); // Reset to first page on new search
        setSelectedImages([]); // Clear selections on search
    };

    // Close Snackbar
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    // Calculate pagination details
    const filteredImages = allImages.filter(image =>
        image.fileName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const totalPages = filteredImages.length > 0 ? Math.ceil(filteredImages.length / rowsPerPage) : 0;
    const isNextButtonDisabled = () => loading || page + 1 >= totalPages;
    const isPreviousButtonDisabled = () => loading || page === 0;

    // --- Loading and Error States ---
    if (loading && allImages.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress sx={{ color: '#2e7d32' }} />
                <Typography variant="h6" ml={2}>Loading gallery...</Typography>
            </Box>
        );
    }

    if (error && allImages.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <motion.div // Apply Framer Motion to the main container
            initial="hidden" // Initial state
            animate="visible" // Animate to visible state
            variants={containerVariants} // Use defined variants
            style={{
                marginTop: '16px', // Matches sx={{ mt: 2 }}
                borderRadius: '8px', // Matches sx={{ borderRadius: 2 }}
                padding: '24px', // Matches sx={{ p: 3 }}
                backgroundColor: '#fff', // Matches sx={{ bgcolor: '#fff' }}
                boxShadow: '0px 2px 4px rgba(0,0,0,0.1)', // Matches sx={{ boxShadow: 1 }}
                fontFamily: '"Inter", sans-serif' // Apply Inter font
            }}
        >
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.0 }}
            >
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                    Gallery
                </Typography>
                <Typography variant="body2" color="#2e7d32" sx={{ mb: 2 }}>
                    Dashboard • Gallery • List
                </Typography>
            </motion.div>

            {/* Top Bar: Search and Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        placeholder="Search by file name..."
                        size="small"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ marginRight: 1, color: '#9ca3af' }} />, // Changed from FiSearch
                        }}
                        sx={{ minWidth: { xs: '100%', sm: '200px' } }}
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        sx={{
                            borderColor: '#9ca3af',
                            color: '#9ca3af',
                            '&:hover': { borderColor: 'tomato', color: 'red' },
                            borderRadius: 1.5 // Rounded corners
                        }}
                        startIcon={<DeleteIcon />}
                        onClick={handleBulkDelete}
                        disabled={selectedImages.length === 0 || loading}
                        component={motion.button} // Apply motion to the button
                        whileHover={{ scale: 1.05 }} // Slight scale on hover
                        whileTap={{ scale: 0.95 }} // Slight press effect on tap
                    >
                        Delete Selected ({selectedImages.length})
                    </Button>
                    {/* Fixed: Moved motion.div wrapper to resolve duplicate 'component' attribute warning */}
                    <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        style={{ display: 'inline-block' }} // Essential for motion to apply correctly
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            component="label" // Keep this for file input association
                            sx={{
                                backgroundColor: '#2e7d32',
                                '&:hover': { backgroundColor: '#45a049' },
                                borderRadius: 1.5
                            }}
                            disabled={loading}
                        >
                            Upload File
                            <Input
                                type="file"
                                sx={{ display: 'none' }} // Hide native input
                                inputProps={{
                                    accept: 'image/*,.svg,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.zip,.rar,.mp3,.mp4,.wav,.avi', // Allowed file types
                                }}
                                onChange={handleImageUpload}
                            />
                        </Button>
                    </motion.div>
                </Box>
            </Box>

            {/* Image Grid */}
            <Grid container spacing={3} columns={{ xs: 4, sm: 8, md: 12, lg: 12 }} sx={{ mt: 2 }}>
                <AnimatePresence> {/* Enable exit animations for children */}
                    {displayImages.length > 0 ? (
                        displayImages.map((image) => (
                            <Grid
                                item // Marks this as a grid item
                                xs={4} // 1/1 column on extra-small screens
                                sm={4} // 1/2 column on small screens (8/2 = 4)
                                md={3} // 1/4 column on medium screens (12/4 = 3)
                                lg={3} // 1/4 column on large screens
                                key={image.id}
                                component={motion.div} // Apply motion to the Grid item
                                variants={itemVariants} // Use item variants
                                initial="hidden"
                                animate="visible"
                                exit="exit" // Use exit variant when unmounting
                                layout // For smooth transitions when items are added/removed
                            >
                                <Card
                                    sx={{
                                        position: 'relative',
                                        boxShadow: 3,
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: 6,
                                        },
                                        height: '100%', // Ensure cards have equal height
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    {/* Checkbox for selection */}
                                    <Checkbox
                                        checked={selectedImages.includes(image.id)}
                                        onChange={(event) => {
                                            const newSelectedImages = event.target.checked
                                                ? [...selectedImages, image.id]
                                                : selectedImages.filter(id => id !== image.id);
                                            setSelectedImages(newSelectedImages);
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            color: '#2e7d32',
                                            '&.Mui-checked': {
                                                color: '#2e7d32',
                                            },
                                            zIndex: 1 // Ensure checkbox is above image
                                        }}
                                    />
                                    {/* Image Media */}
                                    <CardMedia
                                        component="img"
                                        height="140"
                                        image={image.fileUrl || 'https://placehold.co/150x140/E0E0E0/616161?text=No+Image'} // Placeholder for missing images
                                        alt={image.fileName}
                                        sx={{ objectFit: 'cover' }}
                                    />
                                    {/* File Name Content */}
                                    <CardContent sx={{ p: 1, flexGrow: 1 }}>
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            {truncateText(image.fileName)}
                                        </Typography>
                                    </CardContent>
                                    {/* Actions: Copy and Delete */}
                                    <CardActions sx={{ justifyContent: 'space-between', p: 1, borderTop: '1px solid #eee' }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => copyToClipboard(image.fileUrl)}
                                            title="Copy Link"
                                            component={motion.button}
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                        >
                                            <ContentCopyIcon /> {/* Changed from FiCopy */}
                                        </IconButton>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            // startIcon={<DeleteIcon />}
                                            onClick={() => handleSingleDelete(image.id)}
                                            sx={{ borderRadius: 1.5 }}
                                            component={motion.button}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            <DeleteIcon />
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))
                    ) : (
                        <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
                            <Typography variant="h6" color="text.secondary">
                                No files available matching your search.
                            </Typography>
                        </Box>
                    )}
                </AnimatePresence>
            </Grid>

            {/* Pagination Controls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Showing {displayImages.length} of {filteredImages.length} files
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        select
                        size="small"
                        value={rowsPerPage}
                        onChange={handleRowsPerPageChange}
                        label="Files per page"
                        disabled={loading}
                        sx={{ minWidth: 120 }}
                    >
                        {[8, 16, 24].map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            disabled={isPreviousButtonDisabled()}
                            onClick={() => handlePageChange(page - 1)}
                            sx={{ borderRadius: 1.5, borderColor: isNextButtonDisabled() ? 'text.disabled' : '#2e7d32', color: isNextButtonDisabled() ? 'text.disabled' : '#2e7d32' }}
                            component={motion.button}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Previous
                        </Button>
                        <Typography variant="body2" sx={{ alignSelf: 'center', minWidth: '70px', textAlign: 'center' }}>
                            Page {page + 1} of {totalPages || 1}
                        </Typography>
                        <Button
                            variant="outlined"
                            disabled={isNextButtonDisabled()}
                            onClick={() => handlePageChange(page + 1)}
                            sx={{ borderRadius: 1.5, borderColor: isNextButtonDisabled() ? 'text.disabled' : '#2e7d32', color: isNextButtonDisabled() ? 'text.disabled' : '#2e7d32' }}
                            component={motion.button}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Next
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Snackbar for Notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 1.5, boxShadow: 6 }} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDeleteDialog}
                onClose={handleCloseDeleteDialog}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle id="delete-dialog-title" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                    {"Confirm Deletion"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        {isBulkDelete
                            ? `Are you sure you want to delete ${itemsToDelete.length} selected file(s)? This action cannot be undone.`
                            : `Are you sure you want to delete this file? This action cannot be undone.`
                        }
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} sx={{ color: 'text.secondary' }}>Cancel</Button>
                    <Button onClick={handleDeleteConfirmed} autoFocus variant="contained" color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </motion.div>
    );
};

export default GalleryList;
